import { type ChangeEvent, useEffect, useMemo, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useCommercialStructure } from 'commons/customHooks'
import {
  Accordion,
  Alert,
  Breadcrumb,
  Card,
  Icon,
  Input,
  LoadingModal,
  Modal,
  Page,
  Select,
  SlimButton,
  Typography,
  UploadArea,
} from 'components'
import {
  type Channels,
  Company,
  companies,
  type Offices,
  Organization,
  sectorOptions,
} from 'constants/enums'
import { ROUTES } from 'constants/urls'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import type { FranchiseFormValues } from 'services/batchOrder/interfaces'
import {
  clearGeneratedData,
  clearUploadData,
  deleteFranchiseUpload,
  SendFranchiseOrders,
  sendFranchiseUpload,
} from 'store/BatchOrderContext/BatchOrderSlice'
import { getCommercialStructure, getQuotasByOffice } from 'store/SharedContext/sharedSlice'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import Footer from '../components/Footer'
import UploadSpreadsheetTableError from '../components/UploadSpreadsheetTableError'
import ModalConfirmGeneration from './components/ModalConfirmGeneration'
import * as S from './styles'
import { formSchema } from './validations'

const Franchises = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    loadingUpload,
    loadingSend,
    loadingDeletion,
    hasConfirmedFranchiseOrders,
    uploadData: { processId, franchiseErrors, uploadSuccess, hasOrders, errorDownloadLink },
  } = useSelector((state) => state.batchOrder)

  const { quotas, loadingCommercialStructure, loadingQuotas } = useSelector((state) => state.shared)
  const officesCommercialStructure = useSelector((state) => state.shared.offices)

  const { getMarkets, getOfficesByMarkets, getRegionalsByOffices, getZonesByRegionals } =
    useCommercialStructure()

  const [uploadProgress, setUploadProgress] = useState(0)
  const [modalDeleteOrders, setModalDeleteOrders] = useState(false)
  const [modalConfirmGeneration, setModalConfirmGeneration] = useState(false)

  const hasUploadError = !!processId && !!franchiseErrors.length
  const disableForm =
    loadingUpload || hasOrders || loadingCommercialStructure || loadingQuotas || hasUploadError

  const company = [
    {
      title: companies[Company.ALPA].title,
      value: companies[Company.ALPA].value,
    },
  ]

  const defaultValues: FranchiseFormValues = {
    company: Company.ALPA,
    file: null,
    finalClientCol: '171',
    finalSizeCol: '50',
    initialClientCol: '52',
    initialDataLine: '8',
    initialSizeCol: '23',
    market: '',
    office: '',
    organization: Organization.ALHV,
    quotaMonth: '',
    region: '',
    sector: '',
    zone: '',
  }

  const {
    control,
    getValues,
    watch,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useForm<FranchiseFormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })

  const markets = getMarkets()
  const selectedMarket = watch('market') as Channels
  const selectedSector = watch('sector')
  const selectedOffice = watch('office') as Offices
  const selectedRegion = watch('region')
  const file = watch('file')
  const fileName = useMemo(() => file?.name ?? '', [file])

  const onSubmit = handleSubmit((data) => {
    dispatch(sendFranchiseUpload(data, setUploadProgress))
  })

  const handleOrderDeletion = () => {
    setModalDeleteOrders(false)
    dispatch(deleteFranchiseUpload(fileName))
  }

  const handleOrderSend = () => {
    dispatch(SendFranchiseOrders(fileName))
  }

  const handleErrorDownload = () => {
    if (errorDownloadLink) {
      window.open(errorDownloadLink, '_blank')
    }
  }

  useEffect(() => {
    if (!officesCommercialStructure.length) {
      dispatch(getCommercialStructure())
    }
  }, [officesCommercialStructure])

  useEffect(() => {
    dispatch(clearUploadData())
    dispatch(clearGeneratedData())
  }, [])

  useEffect(() => {
    if (selectedOffice) {
      dispatch(getQuotasByOffice(selectedOffice))
    }
  }, [selectedOffice])

  useEffect(() => {
    if (hasConfirmedFranchiseOrders) {
      setModalConfirmGeneration(true)
    }
  }, [hasConfirmedFranchiseOrders])

  const officeOptions = useMemo(() => {
    if (!selectedMarket) return []

    return getOfficesByMarkets([selectedMarket])
  }, [selectedMarket])

  const regionOptions = useMemo(() => {
    return getRegionalsByOffices([selectedOffice])
  }, [selectedOffice])

  const zoneOptions = useMemo(() => {
    return getZonesByRegionals([selectedRegion])
  }, [selectedRegion])

  const validateNumber = (value: string) => {
    const regex = /^\d+$/

    return regex.test(value) || value === ''
  }

  const InsertionHeader = () => (
    <S.ColWrapper>
      <Typography design="hav-h6-text">Inserção de material</Typography>
      <Typography design="hav-h7-text">
        Após preencher os campos acima, carregue a planilha e solicite os materiais.
      </Typography>
    </S.ColWrapper>
  )

  const SpreadsheetInfo = () => (
    <S.InfoWrapper>
      <Icon color={theme.colors.primary.main} icon="info" size="16" />
      <Typography design="hav-default-03-text">
        Certifique-se de que a planilha carregada tenha o pedido na primeira aba, pois apenas ela
        será processada.
      </Typography>
    </S.InfoWrapper>
  )

  return (
    <Page fullwidth>
      <Breadcrumb />
      <S.Wrapper>
        <Accordion summaryContent={<S.Title>Pedido franquias massivo</S.Title>}>
          <S.Form onSubmit={onSubmit}>
            <S.Field>
              <Controller
                control={control}
                defaultValue={Company.ALPA}
                name="company"
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    disabled
                    id="company"
                    label="Empresa"
                    name={name}
                    onChange={(e) => onChange(e)}
                    options={company}
                    value={value}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                control={control}
                name="organization"
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    disabled
                    id="organization"
                    label="Organização de venda"
                    name={name}
                    onChange={(e) => onChange(e)}
                    options={[{ title: 'ALHV - Havaianas', value: Organization.ALHV }]}
                    value={value}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                control={control}
                name="market"
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    disabled={disableForm}
                    id="market"
                    label="Mercado"
                    menuText="Clique para selecionar"
                    name={name}
                    onChange={(e) => {
                      setValue('office', '')
                      setValue('region', '')
                      setValue('zone', '')
                      setValue('quotaMonth', '')
                      onChange(e)
                    }}
                    options={markets.map((market) => ({
                      title: market.id,
                      value: market.id,
                    }))}
                    value={value}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                control={control}
                name="sector"
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    disabled={!selectedMarket || disableForm}
                    id="sector"
                    label="Setor"
                    menuText={!selectedMarket ? 'Selecione um mercado' : 'Clique para selecionar'}
                    name={name}
                    onChange={(e) => onChange(e)}
                    options={sectorOptions}
                    value={value}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                control={control}
                name="office"
                render={({ field: { name, value, onChange }, fieldState }) => (
                  <Select
                    disabled={!officeOptions.length || !selectedSector || disableForm}
                    error={fieldState.error?.message}
                    id="office"
                    label="Escritório de venda"
                    menuText={!selectedSector ? 'Selecione um setor' : 'Clique para selecionar'}
                    name={name}
                    onChange={(e) => {
                      setValue('region', '')
                      setValue('zone', '')
                      onChange(e)
                    }}
                    options={officeOptions.map((office) => ({
                      title: `${office.id} - ${office.description}`,
                      value: office.id,
                    }))}
                    value={value}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                control={control}
                name="region"
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    disabled={!regionOptions.length || !selectedOffice || disableForm}
                    id="region"
                    label="Regional"
                    menuText={
                      !selectedOffice ? 'Selecione um escritório' : 'Clique para selecionar'
                    }
                    name={name}
                    onChange={(e) => {
                      setValue('zone', '')
                      onChange(e)
                    }}
                    options={regionOptions.map((region) => ({
                      title: `${region.id} - ${region.description}`,
                      value: region.id,
                    }))}
                    value={value}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                control={control}
                name="zone"
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    disabled={!zoneOptions.length || !selectedRegion || disableForm}
                    id="zone"
                    label="Zona"
                    menuText={!selectedRegion ? 'Selecione uma regional' : 'Clique para selecionar'}
                    name={name}
                    onChange={(e) => onChange(e)}
                    options={zoneOptions.map((zone) => ({
                      title: `${zone.id} - ${zone.description}`,
                      value: zone.id,
                    }))}
                    value={value}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                control={control}
                name="quotaMonth"
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    disabled={!quotas.length || !selectedOffice || disableForm}
                    id="quotaMonth"
                    label="Mês cota"
                    menuText={
                      !selectedOffice ? 'Selecione um escritório' : 'Clique para selecionar'
                    }
                    name={name}
                    onChange={(e) => onChange(e)}
                    options={quotas.map((quota) => ({
                      title: quota.MesCota,
                      value: quota.Descricao,
                    }))}
                    value={value}
                  />
                )}
              />
            </S.Field>
            <S.HozDivider />
            <S.Field span={1}>
              <S.SizeWrapper>
                <Typography design="hav-h8-text">Dados: Linha inicial</Typography>
                <Typography design="hav-default-03-text">
                  {getValues('initialDataLine') ?? '-'}
                </Typography>
              </S.SizeWrapper>
            </S.Field>
            <S.Field span={1}>
              <S.SizeWrapper>
                <Typography design="hav-h8-text">Tamanho: Coluna inicial</Typography>
                <Typography design="hav-default-03-text">
                  {getValues('initialSizeCol') ?? '-'}
                </Typography>
              </S.SizeWrapper>
            </S.Field>
            <S.Field span={1}>
              <Controller
                control={control}
                name="finalSizeCol"
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    disabled={disableForm}
                    error={fieldState.error?.message}
                    label="Tamanho: Coluna final"
                    maxLength={4}
                    onChange={(e) => {
                      if (validateNumber(e.target.value)) field.onChange(e)
                    }}
                  />
                )}
              />
            </S.Field>
            <S.Field span={1}>
              <Controller
                control={control}
                name="initialClientCol"
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    disabled={disableForm}
                    error={fieldState.error?.message}
                    label="Cliente: Coluna inicial"
                    maxLength={4}
                    onChange={(e) => {
                      if (validateNumber(e.target.value)) field.onChange(e)
                    }}
                  />
                )}
              />
            </S.Field>
            <S.Field span={1}>
              <S.SizeWrapper>
                <Typography design="hav-h8-text">Cliente: Coluna final</Typography>
                <Typography design="hav-default-03-text">
                  {getValues('finalClientCol') ?? '-'}
                </Typography>
              </S.SizeWrapper>
            </S.Field>
          </S.Form>
        </Accordion>
        {hasUploadError && !hasOrders && (
          <Alert
            fullWidth
            kind="error"
            message="Foram identificados alguns erros na planilha. Verifique os detalhes abaixo e faça as correções necessárias antes de reenviá-la."
            title="Erros na planilha!"
          />
        )}
        {hasUploadError && hasOrders && (
          <Alert
            fullWidth
            kind="warning"
            message='Confira abaixo e clique em "Confirmar" caso deseje continuar mesmo assim. Caso contrário, os pedidos ficarão em aberto na área de gerenciamento.'
            title="Foram identificados alguns erros na planilha!"
          />
        )}
        {!hasUploadError && hasOrders && (
          <Alert
            fullWidth
            kind="info"
            message='Clique em "solicitar" para gerar as OVs. Caso contrário, os pedidos ficarão em aberto na área de gerenciamento.'
            title="Pedido em aberto!"
          />
        )}
        {!processId && (
          <Card>
            <S.InsertionWrapper>
              <InsertionHeader />
              <SpreadsheetInfo />
              {isValid && (
                <>
                  <S.Divider />
                  <Controller
                    control={control}
                    defaultValue={{} as File}
                    name="file"
                    render={({ field: { name, onChange } }) => (
                      <UploadArea
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        id="file"
                        loading={loadingUpload}
                        loadingProgress={uploadProgress}
                        name={name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (e.target.files) {
                            onChange(e.target.files[0])
                            onSubmit()
                            e.target.value = ''
                          }
                        }}
                        uploadAreaText={
                          <>
                            <strong>Clique aqui</strong> ou arraste o arquivo da sua planilha para
                            enviá-la.
                          </>
                        }
                      />
                    )}
                  />
                </>
              )}
            </S.InsertionWrapper>
          </Card>
        )}
        {hasUploadError && (
          <Card>
            <S.ErrorWrapper>
              <S.ErrorOrderHeader>
                <S.ErrorOrderActions>
                  {!hasOrders && (
                    <SlimButton
                      content="Voltar"
                      icon={<Icon icon="undo" size="15" />}
                      onClick={() => dispatch(clearUploadData())}
                      type="button"
                      variant="primary"
                    />
                  )}
                  <SlimButton
                    content="Baixar"
                    icon={<Icon icon="download" size="15" />}
                    onClick={handleErrorDownload}
                    type="button"
                    variant="primary"
                  />
                </S.ErrorOrderActions>
                {hasOrders && (
                  <Footer
                    amountOfBoxes={uploadSuccess.boxVol}
                    amountOfPairs={uploadSuccess.pairVol}
                    loading={loadingSend || loadingDeletion}
                    onDelete={() => setModalDeleteOrders(true)}
                    onSubmit={handleOrderSend}
                    totalValue={uploadSuccess.totalVal}
                  />
                )}
              </S.ErrorOrderHeader>
              <S.UploadedFileViewer>
                <Icon color={theme.colors.primary.main} icon="paper-clip" size="15" />
                <Typography design="hav-h7-text">{fileName}</Typography>
              </S.UploadedFileViewer>
              <UploadSpreadsheetTableError data={franchiseErrors} />
            </S.ErrorWrapper>
          </Card>
        )}
        {hasOrders && !hasUploadError && (
          <Card>
            <S.InsertionWrapper>
              <InsertionHeader />
              <SpreadsheetInfo />
              <S.Divider />
              <S.UploadedFileViewer>
                <Icon color={theme.colors.primary.main} icon="paper-clip" size="15" />
                <Typography design="hav-h7-text">{fileName}</Typography>
              </S.UploadedFileViewer>
              <Footer
                amountOfBoxes={uploadSuccess.boxVol}
                amountOfPairs={uploadSuccess.pairVol}
                loading={loadingSend || loadingDeletion}
                onDelete={() => setModalDeleteOrders(true)}
                onSubmit={handleOrderSend}
                totalValue={uploadSuccess.totalVal}
              />
            </S.InsertionWrapper>
          </Card>
        )}
        <Modal
          description="Esta ação excluirá o pedido em aberto e não poderá ser desfeita."
          open={modalDeleteOrders}
          setOpen={setModalDeleteOrders}
          showAction={false}
          title="Excluir"
        >
          <S.DeleteOrdersActions>
            <SlimButton
              content="Voltar"
              onClick={() => setModalDeleteOrders(false)}
              variant="ghost"
            />
            <SlimButton
              content="Excluir solicitação"
              loading={loadingDeletion}
              onClick={handleOrderDeletion}
              variant="danger"
            />
          </S.DeleteOrdersActions>
        </Modal>
        {loadingSend && <LoadingModal loadingText="Solicitando materiais..." />}
        <ModalConfirmGeneration
          onConfirm={() => {
            dispatch(clearGeneratedData())
            setModalConfirmGeneration(false)
          }}
          onReturn={() => {
            dispatch(clearGeneratedData())
            navigate(`${ROUTES.manage.base}`)
          }}
          open={modalConfirmGeneration}
          setOpen={setModalConfirmGeneration}
        />
      </S.Wrapper>
    </Page>
  )
}

export default Franchises
